// Original macroeconomic-literacy lessons; all figures and release scenarios are hypothetical.
export const marketLessons = [
  {
    id: 'cpi-inflation', moduleId: 'markets',
    title: '通膨降了，為什麼買東西還是貴？', subtitle: 'CPI 水準、年增率與基期', minutes: 7, tag: '讀懂物價',
    objectives: ['分開閱讀價格水準與價格上漲速度', '核對年增率的比較月份與分母'],
    paragraphs: [
      '消費者物價指數（CPI）用一籃子消費商品與服務的價格，衡量整體價格隨時間的變化。它是一個加權統計，不是每件商品都一起漲，也不會完全等於每個家庭的支出變化；你的房租、飲食與交通比重可能和統計籃子不同。',
      'CPI 年增率通常是「本月指數 ÷ 去年同月指數 − 1」。年增由 8% 降至 3%，表示和一年前相比的上漲速度放慢；只要仍為正，整體價格水準就仍高於去年同月。這和指數本身下跌不同。月增率則比較上個月，不能和年增率直接互換。',
      '年增率也受去年比較基期影響。去年某個月價格很高，成為今年的分母，就可能壓低年增率；某次大幅變動離開十二個月比較區間，也會改變讀數。看新聞時，同時檢查指數水準、月增與年增，以及有無季節調整，不要只用「通膨下降」四個字推論生活費已變便宜。',
    ],
    example: {
      title: '假設：連續三年同月份的 CPI 指數',
      steps: ['甲年指數 100，乙年同月 110，乙年的年增率為 110 ÷ 100 − 1 = 10%。', '丙年同月指數 113.3，年增率為 113.3 ÷ 110 − 1 = 3%。', '年增從 10% 放慢至 3%，但價格指數仍從 110 上升到 113.3；相對甲年，累積上漲為 13.3%，不是回到 100。'],
      conclusion: '漲得比較慢，和價格已下跌，是兩個不同敘述。',
    },
    pitfalls: ['把年增率降低誤讀為 CPI 指數下降，進而認為每項生活費都變便宜。', '把未季調年增率和另一項季調月增率混在一起比較，沒有查口徑。'],
    glossary: [{ term: '價格水準', definition: '某一時點價格指數的高低；成長率則描述兩個時點之間的相對變化。' }, { term: '基期效應', definition: '比較期間的數值高低會影響成長率，使最新數字需要連同比較分母一起解讀。' }],
    takeaway: '先分清楚物價是「漲得慢了」，還是「指數真的下跌」。',
    exercise: '找一份 CPI 官方報告，記下資料月份、指數值、月增與年增；用去年同月指數驗算年增，再看看自己常買的兩類項目是否同方向。',
    quiz: { question: '去年同月 CPI 為 110，今年為 112.2，最正確的敘述是什麼？', options: ['物價整體下跌 2%', '物價指數比去年同月高 2%', '每一種商品都一定上漲 2%'], answer: 1, explanation: '112.2 ÷ 110 − 1 = 2%。這是整體指數的年增率，不表示所有商品或每個人的支出都同幅度上漲。' },
    sources: [
      { label: '市場先生｜CPI 消費者物價指數', url: 'https://rich01.com/what-is-cpi-index/', kind: 'reading' },
      { label: 'BLS｜CPI 百分比變動的計算方式', url: 'https://www.bls.gov/cpi/factsheets/calculating-percent-changes.htm', kind: 'official' },
      { label: 'BLS｜CPI 籃子、用途與限制', url: 'https://www.bls.gov/cpi/questions-and-answers.htm', kind: 'official' },
    ],
  },
  {
    id: 'real-interest-rates', moduleId: 'markets',
    title: '領到利息，購買力一定增加嗎？', subtitle: '名目利率、實質利率與通膨預期', minutes: 7, tag: '購買力',
    objectives: ['用同期間通膨把名目利息換成購買力變化', '區分事前預期與事後實現的實質利率'],
    paragraphs: [
      '本課的名目利率，指沒有扣除通膨的一年有效利率；它描述金額增加多少。實質利率則把同期間物價變化考慮進來，描述以選定物價指數衡量的購買力變化。它和把月利率換成年利率的計息問題不同，不能只看到「名目」就混用公式。',
      '在一年、同幣別且忽略稅費的簡化情境，實質變動率 =（1 + 名目利率）÷（1 + 通膨率）− 1。常見的「名目減通膨」是兩個比率不大時的近似。通膨指數也要和用途相符，整體 CPI 並不保證精確描述你的個人消費籃子。',
      '做決定當下只能使用未來通膨的預期，得到事前的實質利率估計；期間結束後用實際通膨，才得到事後購買力結果。把今天的一年利率減去過去十二個月 CPI，混合了不同時間方向，不能直接稱為未來一年確定的實質收益。',
    ],
    example: {
      title: '假設：年利率 4%，預期與實際通膨不同',
      steps: ['本金 10,000 元，一年名目利率 4%，按約一年後得到 10,400 元；忽略稅、費用及履約風險。', '起初假設未來一年通膨 2%，事前實質利率估計為 1.04 ÷ 1.02 − 1，約 1.96%。', '若實際同期間通膨為 5%，10,400 元折算成期初購買力約為 10,400 ÷ 1.05 = 9,904.76 元；事後實質變動約 −0.95%。'],
      conclusion: '名目利息如期收到，和購買力下降可以同時發生；差別來自實際物價路徑。',
    },
    pitfalls: ['把預期通膨當成已知結果，宣稱未來實質收益已被鎖定。', '未檢查期間與幣別，就把某國利率減去另一國過去通膨。'],
    glossary: [{ term: '事前實質利率', definition: '使用未來同期間通膨預期估計的實質利率，會隨預期改變。' }, { term: '事後實質利率', definition: '使用已實現的同期間物價變化，回算利息的購買力成果。' }],
    takeaway: '實質利率要附上通膨的期間，以及它是預期還是已發生。',
    exercise: '用同一年名目利率 3%，分別代入假設通膨 1%、3%、5%，算精確比值與簡單相減的差異，再註記這些都是情境假設。',
    quiz: { question: '一年名目利率 6%，同期間實際通膨 3%，忽略稅費，精確的事後實質變動約為多少？', options: ['9%', '精確等於 3%', '2.91%'], answer: 2, explanation: '1.06 ÷ 1.03 − 1 約為 2.91%；6% − 3% = 3% 是近似值。' },
    sources: [
      { label: '市場先生｜名目與實質利率', url: 'https://rich01.com/nominal-interest-vs-real-interest/', kind: 'reading' },
      { label: 'ECB｜名目利率與實質利率的差別', url: 'https://www.ecb.europa.eu/ecb-and-you/explainers/tell-me/html/nominal_and_real_interest_rates.en.html', kind: 'official' },
      { label: 'ECB｜價格穩定與預期通膨', url: 'https://www.ecb.europa.eu/pub/pdf/other/whypricestabilityen.pdf', kind: 'official' },
    ],
  },
  {
    id: 'policy-rates', moduleId: 'markets',
    title: '央行降息，不代表所有利率一起下降', subtitle: '政策工具、市場定價與傳導', minutes: 7, tag: '理解政策',
    objectives: ['分辨央行政策設定與實際市場利率', '避免把單一升降息消息當成股價方向保證'],
    paragraphs: [
      '央行以政策工具影響金融條件，但各國制度不同。以美國為例，FOMC 設定聯邦基金利率的目標區間，聯準會運用工具影響隔夜市場利率。目標區間、實際交易形成的有效利率，以及銀行給你的存貸款利率，不是同一個數字。',
      '政策變動通常先影響短期資金條件，再透過借貸成本、支出與金融市場等管道傳導。較長天期的市場利率還反映未來短期利率路徑、通膨預期與持有期限風險的補償；銀行貸款也受信用與契約條件影響。因此不必與一次政策調整同時、同幅度移動。',
      '股價會同時考量企業獲利、折現率、風險與市場原先預期。降息可能伴隨經濟走弱訊息，也可能早已被預期；升息亦可能出現在需求強勁的環境。閱讀政策新聞時，分開記錄決策內容、決策理由與未來指引，不把「降息必漲」或「升息必跌」當作規則。',
    ],
    example: {
      title: '假設：政策與長期利率可以不同方向',
      steps: ['假想央行把隔夜政策目標區間由 4.50%～4.75% 調為 4.25%～4.50%，上下限都下降 0.25 個百分點，也就是 25 基點。', '同一段假設期間，十年期公債殖利率卻由 3.6% 上升到 3.8%，增加 20 基點。這並不在數學或制度上矛盾。', '讀者接著應查市場是否調整了通膨、未來政策路徑或期限風險的看法；僅憑這兩個數字，無法判定是哪個因素主導。'],
      conclusion: '政策設定是一個輸入，市場價格反映多種預期；例題不是任何實際決策或行情。',
    },
    pitfalls: ['把政策目標區間直接當成自己的定存利率或房貸利率。', '只看決策升降幅度，沒有區分市場早已預期的部分與新的訊息。'],
    glossary: [{ term: '政策利率', definition: '央行為執行貨幣政策所設定或主要操作的利率，實際制度依國家而異。' }, { term: '貨幣政策傳導', definition: '政策透過利率、金融條件及支出決策等管道影響經濟的過程。' }],
    takeaway: '先辨認政策調整的是哪個利率，再追查它如何影響其他條件。',
    exercise: '找一份央行決策公告，把「已決定的措施」、「對經濟的判斷」與「未來取決於什麼」各寫一句，不推導買賣結論。',
    quiz: { question: '政策利率從 4.75% 降至 4.50%，正確的幅度描述是什麼？', options: ['下降 25 基點，即 0.25 個百分點', '下降 25 個百分點', '所有十年期債券殖利率都必定下降 0.25 個百分點'], answer: 0, explanation: '4.75% − 4.50% = 0.25 個百分點 = 25 基點。政策變動不代表所有期限與信用條件的市場利率都同幅移動。' },
    sources: [
      { label: '市場先生｜Fed 與市場利率運作', url: 'https://rich01.com/fed-interest-rate-work/', kind: 'reading' },
      { label: 'Federal Reserve｜貨幣政策目標與運作方式', url: 'https://www.federalreserve.gov/monetarypolicy/monetary-policy-what-are-its-goals-how-does-it-work.htm', kind: 'official' },
      { label: 'Federal Reserve｜政策傳導與金融條件', url: 'https://www.federalreserve.gov/aboutthefed/files/pf_complete.pdf', kind: 'official' },
    ],
  },
  {
    id: 'yield-curve', moduleId: 'markets',
    title: '殖利率曲線畫的是時間嗎？', subtitle: '期限結構、利差與倒掛', minutes: 7, tag: '閱讀曲線',
    objectives: ['在同一觀察時點比較不同到期期限', '計算長短期利差並理解倒掛訊號的限制'],
    paragraphs: [
      '殖利率曲線通常以剩餘期限為橫軸、殖利率為縱軸，把同一觀察時點的不同期限放在一起。它不是某張債券過去十年的價格走勢。比較時應固定幣別、發行人或信用基礎及資料方法，避免把公司債信用風險誤當成期限差異。',
      '較長期限殖利率高於較短期限時，這一段曲線向上；反過來則稱倒掛。說倒掛要指出比較哪兩個期限，例如十年減兩年，或十年減三個月；曲線也可能某段向上、另一段向下。長短期利差是相減的百分點差，不是長債未來一定比短債多賺的報酬。',
      '曲線包含市場對未來利率與經濟的預期，也受期限風險補償、資產供需等因素影響。歷史研究曾利用特定公債期限利差估計衰退機率，但模型有樣本與假設限制；倒掛不是確定衰退，更不能保證衰退日期、嚴重程度或股市漲跌時點。',
    ],
    example: {
      title: '假設：把同一天的公債報價放在一起',
      steps: ['同幣別、同一政府公債資料，兩年期殖利率 4.2%，十年期 3.7%。十年減兩年 = −0.5 個百分點 = −50 基點，這一段倒掛。', '另一個觀察日，兩年期變為 3.8%，十年期仍為 3.7%，利差變成 −10 基點。', '利差增加 40 基點、倒掛程度縮小，但仍是負值。僅憑此變化，不能宣布經濟衰退已經結束。'],
      conclusion: '每張曲線是某個時點的期限比較；觀察兩天變化時，先核對每個期限各自怎麼動。',
    },
    pitfalls: ['把十年期的殖利率誤看成未來十年股市預測。', '只看到「轉正」或「倒掛」標題，沒有查使用哪兩個期限與哪種資料。'],
    glossary: [{ term: '期限利差', definition: '不同到期期限的殖利率相減，本課固定用較長期限減較短期限。' }, { term: '倒掛', definition: '在指定比較中，較長期限殖利率低於較短期限，不必代表整條曲線每一段都向下。' }],
    takeaway: '曲線先告訴你期限間的定價，再提供需要其他資料佐證的經濟線索。',
    exercise: '找一張官方公債殖利率表，只選同一天的資料，標出三個月、兩年與十年，再分別算十年減兩年、十年減三個月，避免混用日期。',
    quiz: { question: '同一天十年期殖利率 3.4%、兩年期 3.9%，十年減兩年的利差是多少？', options: ['+50 基點', '−50 基點', '−0.5 基點'], answer: 1, explanation: '3.4% − 3.9% = −0.5 個百分點 = −50 基點。這說明該期限組合倒掛，沒有提供衰退必然發生的結論。' },
    sources: [
      { label: '市場先生｜殖利率曲線與倒掛', url: 'https://rich01.com/what-is-yield-curve-inversion-0/', kind: 'reading' },
      { label: 'New York Fed｜殖利率曲線模型與使用說明', url: 'https://www.newyorkfed.org/research/capital_markets/ycfaq', kind: 'official' },
      { label: 'New York Fed｜以殖利率曲線預測景氣的實務限制', url: 'https://www.newyorkfed.org/medialibrary/media/research/current_issues/ci12-5.pdf', kind: 'official' },
    ],
  },
  {
    id: 'gdp-growth', moduleId: 'markets',
    title: 'GDP 成長 3%，先確認是哪種 3%', subtitle: '名目、實質、年增與季增年率', minutes: 8, tag: '核對分母',
    objectives: ['區分價格造成的產值變化與實質產出變化', '辨認 YoY、QoQ 與季增年率的計算期間'],
    paragraphs: [
      '國內生產毛額（GDP）衡量一段期間內，經濟體境內生產的最終商品與服務價值，不能把供應鏈每一階段的銷售額全部重複加總。名目 GDP 使用當期價格，變動同時包含數量與價格；實質 GDP 經價格調整，較適合觀察產出量變化。',
      '只產一種商品的假想經濟，數量增加 2%、單價增加 3%，名目產值就增加 1.02 × 1.03 − 1 = 5.06%，而數量只增加 2%。實際 GDP 涵蓋許多商品並有專門的價格調整方法，不能隨意用 CPI 除名目 GDP，就宣稱得到官方實質 GDP。',
      '年增率 YoY 比去年同期；季增率 QoQ 比上一季，常須核對季節調整。美國新聞常見的季增年率，將本季成長速度假設連續維持四季再換算，並不是本季相對去年同期的增幅，也不是對未來一年的預測。國際比較前，先把實質或名目、期間及年化方式對齊。',
    ],
    example: {
      title: '假設：同一條季調實質 GDP 指數有三種增幅',
      steps: ['上一季指數 100，本季 101.5，季增率 = 101.5 ÷ 100 − 1 = 1.5%。', '若換成季增年率，計算 1.015⁴ − 1，約 6.14%；這是假設同樣季增幅維持四季的換算。', '若同一資料系列的去年同季指數為 98，本季年增率 = 101.5 ÷ 98 − 1，約 3.57%。'],
      conclusion: '1.5%、6.14% 與 3.57% 都可以描述同一季，但分母與換算方式不同。',
    },
    pitfalls: ['把某國季增年率與另一國年增率直接排名，卻沒有統一口徑。', '將名目 GDP 增加全部當成產出量增加，忽略價格變化。'],
    glossary: [{ term: '季節調整', definition: '用統計方法降低季節性重複模式的影響，以便比較相鄰期間，仍可能後續修正。' }, { term: '季增年率', definition: '以本季相對上季的成長因子連乘四次再減 1，通常搭配季調資料；不是全年預測。' }],
    takeaway: 'GDP 標題先補齊四件事：名目或實質、比較期間、季調與年化。',
    exercise: '找兩篇 GDP 新聞，在數字旁補上實質／名目、YoY／QoQ／季增年率、季調狀態及資料季度；找不到的欄位回官方表格查證。',
    quiz: { question: '季調實質 GDP 比上一季增加 1%，換算季增年率應如何計算？', options: ['直接視為年增率 1%', '精確等於 1% × 4 = 4%', '1.01⁴ − 1，約 4.06%'], answer: 2, explanation: '四季成長因子連乘得到約 4.06%。乘 4 是近似，而年增率需要去年同季的數值，不能由此直接得知。' },
    sources: [
      { label: '市場先生｜GDP 與經濟成長率', url: 'https://rich01.com/what-is-gdp-vs-gnp/', kind: 'reading' },
      { label: 'BEA｜GDP、名目與實質概念', url: 'https://www.bea.gov/resources/learning-center/what-to-know-gdp', kind: 'official' },
      { label: 'BEA｜季度變動為何換算成年率', url: 'https://www.bea.gov/help/faq/122', kind: 'official' },
    ],
  },
  {
    id: 'pmi-cycle', moduleId: 'markets',
    title: 'PMI 變成 55，代表經濟成長 55%？', subtitle: '擴散指數與景氣方向', minutes: 7, tag: '調查指標',
    objectives: ['用增加、持平與減少的回答理解擴散指數', '分開看高低於 50 與指數本身升降'],
    paragraphs: [
      '採購經理人指數（PMI）來自企業調查，用訂單、生產、就業等條件的變化觀察景氣。它主要描述變動方向的分布，不是直接加總 GDP，也不是企業營收成長率。不同發布機構、製造業與服務業的樣本及組成可能不同，要先確認你看的完整名稱。',
      '理解擴散指數，可先用一個簡化的「較上月增加、持平、減少」問題：指數 = 增加者百分比 + 0.5 × 持平者百分比。50 表示增加與減少的回答比例相同，不代表所有人都沒變；高於 50 表示增加回答較多。正式綜合 PMI 還會結合多個分項、權重與季調，不能用單一問題假裝重建全部官方結果。',
      'PMI 從 56 降至 52，仍在 50 以上，通常表示該調查所描述的擴張放慢；從 46 升至 49，則可能是收縮程度減輕，而非已轉擴張。製造業指數也不代表整個服務業與全國經濟；供應商交期等分項有特殊方向，閱讀細項時要查定義。',
    ],
    example: {
      title: '假設：100 家企業回答同一個產出變化問題',
      steps: ['40 家表示較上月增加、30 家持平、30 家減少；簡化擴散指數 = 40 + 0.5 × 30 = 55。', '55 表示增加回答較減少回答多，不代表每家產出增加 55%，也不知道各家增加或減少的實際金額。', '若下一次有 30 家增加、40 家持平、30 家減少，指數為 30 + 0.5 × 40 = 50；仍有 60 家企業的情況在變動。'],
      conclusion: '指數點數描述回答分布。方向、變動幅度與產出的實際成長率需要分開看。',
    },
    pitfalls: ['將 PMI 55 讀成 GDP 或工廠產量成長 55%。', '把指數由 47 升到 49 誤稱已經擴張，沒有同時看 50 的位置。'],
    glossary: [{ term: '擴散指數', definition: '把不同方向的調查回答彙整成一個指標，本課簡化公式給持平回答一半權重。' }, { term: '綜合 PMI', definition: '依發布機構方法結合多個調查分項的指數，和單一題目的結果不同。' }],
    takeaway: 'PMI 看方向分布及變化，不把點數當成百分比成長率。',
    exercise: '用 100 份回答，自己設計一組指數 45、一組 50、一組 55 的增加／持平／減少人數；確認每組合計 100，再比較同樣 50 是否有不同回答組合。',
    quiz: { question: '簡化調查中，30% 回答增加、50% 持平、20% 減少，擴散指數是多少？', options: ['55', '30', '80'], answer: 0, explanation: '30 + 0.5 × 50 = 55。這是擴散指數值，不是產出成長 55%，也不是說 55% 企業回答增加。' },
    sources: [
      { label: '市場先生｜PMI 採購經理人指數', url: 'https://rich01.com/what-is-pmi-index/', kind: 'reading' },
      { label: 'ISM｜擴散指數計算與季節調整方法', url: 'https://www.ismworld.org/supply-management-news-and-reports/reports/seasonal-adjustment-factors/', kind: 'official' },
      { label: 'ISM｜製造業 PMI 報告的方法說明', url: 'https://www.ismworld.org/globalassets/pub/research-and-surveys/rob/pmi/f3dz202604pmi.pdf', kind: 'official' },
    ],
  },
  {
    id: 'labor-market', moduleId: 'markets',
    title: '失業率下降，可能沒有新增工作？', subtitle: '勞動力、失業率與參與率', minutes: 8, tag: '看懂分母',
    objectives: ['分別以勞動力與調查涵蓋人口作為分母', '辨認失業率改善是否伴隨就業或參與率改變'],
    paragraphs: [
      '失業人口不是所有沒有工作的人。以勞動力調查的常見概念來說，通常還須符合可工作與積極求職等條件；另有特定例外，依原統計規則判定。就業人口加失業人口構成勞動力，不在這兩類者列為非勞動力，例如未求職且符合該分類的人。',
      '失業率 = 失業人口 ÷ 勞動力；勞動參與率 = 勞動力 ÷ 調查所涵蓋的人口。兩個分母不同，不能拿失業人口除以全國總人口，就稱為官方失業率。各國年齡範圍與涵蓋對象可能不同，跨國比較時要核對定義。',
      '失業率降低，可能來自找到工作，也可能來自停止求職而退出勞動力。反過來，更多人重新求職時，即使就業增加，失業率仍可能上升。閱讀報告應同看就業、失業與參與率；薪資職位數等企業調查，也不能直接當成家庭調查的就業人數。',
    ],
    example: {
      title: '假設：就業人數沒有變，失業率卻降低',
      steps: ['調查涵蓋人口 1,000 人，其中就業 570 人、符合失業定義 30 人；勞動力 600 人，失業率 30 ÷ 600 = 5%，參與率 600 ÷ 1,000 = 60%。', '下一期有 10 名原失業者停止求職，假設符合退出勞動力的分類，其餘條件不變。失業剩 20 人、勞動力變 590 人，就業仍是 570 人。', '失業率降到 20 ÷ 590，約 3.39%；參與率也降到 59%。沒有任何人因這個假設而新找到工作。'],
      conclusion: '失業率下降需要拆開分子與分母，才能理解勞動市場究竟發生什麼。',
    },
    pitfalls: ['把退休、就學或沒有積極求職的人，一律計入官方失業人口。', '只看失業率單一數字，就認定就業人數一定增加或薪資一定改善。'],
    glossary: [{ term: '勞動力', definition: '符合統計定義的就業人口加失業人口。' }, { term: '勞動參與率', definition: '勞動力占該統計所涵蓋人口的比例，反映參與就業或求職的人口比重。' }],
    takeaway: '失業率看求職市場，參與率看誰還在其中，兩者要一起讀。',
    exercise: '沿用例題，改成 10 名失業者找到工作而非退出勞動力，重算失業率與參與率；比較兩種情境中哪個分母有改變。',
    quiz: { question: '調查涵蓋人口 1,000 人，就業 760 人、失業 40 人，失業率與勞動參與率分別是多少？', options: ['4% 與 80%', '5% 與 80%', '5% 與 76%'], answer: 1, explanation: '勞動力 = 760 + 40 = 800；失業率 40 ÷ 800 = 5%，參與率 800 ÷ 1,000 = 80%。4% 把失業率的分母用錯了。' },
    sources: [
      { label: '市場先生｜美國就業數據的主題閱讀', url: 'https://rich01.com/unemployment-rate-3/', kind: 'reading' },
      { label: 'BLS｜勞動力、失業與參與率定義', url: 'https://www.bls.gov/cps/definitions.htm', kind: 'official' },
      { label: 'BLS｜政府如何衡量失業', url: 'https://www.bls.gov/cps/cps_htgm.htm', kind: 'official' },
    ],
  },
  {
    id: 'economic-releases', moduleId: 'markets',
    title: '新聞今天公布，數據描述的是哪一天？', subtitle: '公布時間、資料期間與修正版本', minutes: 8, tag: '查證新聞',
    objectives: ['分開記錄公布日、資料所屬期及版本', '比較預估差異時保持口徑一致並避免事後偏誤'],
    paragraphs: [
      '經濟新聞的日期通常是公布日，不是所有經濟活動發生的日期。某月公布的就業報告可能描述前一個月；某季 GDP 初值則在季度結束後發布。閱讀時先記下發布機構、資料所屬期、公布時間與時區，再確認指標的單位、季調及年化方式。',
      '初步估計常在資料尚未完全到齊時發布，後續取得更多回覆、完整資料或更新統計方法後可能修正。上修或下修是同一段經濟活動的估計更新，不是新一期又增加或減少相同幅度。回顧當時決策時，也要保留當時可見的版本，避免用後來數字假裝當時就知道。',
      '新聞中的市場共識或預估，是調查或分析的比較基準，須確認來源、調查時間與計算方式，不等於官方結果。公布值高於預估，和比前期改善，是兩種比較。即使有預期落差，價格反應仍受細項、其他消息與原先定價影響，不能直接把正負差異變成保證有效的交易訊號。',
    ],
    example: {
      title: '假設：同一季 GDP 的兩次發布',
      steps: ['假想 4 月 28 日發布 1～3 月實質 GDP 的季增年率初值 1.8%；資料所屬期是第一季，不是 4 月 28 日一天。', '假設同口徑預估為 2.1%，公布值低 0.3 個百分點。這不表示 GDP 比上一季下降 0.3%；初值本身仍為正成長。', '5 月 27 日把同一季估計修正為 2.2%，相對初值上修 0.4 個百分點；這不是四、五月額外成長 0.4%。', '整理當時新聞筆記時，4 月 28 日那一頁仍應留下當時可取得的 1.8%，再另外標注後續修正 2.2%。'],
      conclusion: '發布、比較與修正都要放在正確時間線上，才能分辨當時資訊和事後知道的事。',
    },
    pitfalls: ['把公布值減市場預估的差，誤稱為經濟實際成長率。', '做歷史驗證時直接使用最新修正版，忽略當時尚無法取得那些資訊。'],
    glossary: [{ term: '資料版本', definition: '同一所屬期數據在某次公布時的估計值；後續發布可能改變。' }, { term: '預期落差', definition: '公布結果與事先預估之間的差異，需統一指標、期間及單位才有可比性。' }],
    takeaway: '每個經濟數字都帶著兩個時間：描述何時，以及何時才被知道。',
    exercise: '選一篇經濟新聞，寫下公布日、資料所屬期、初值或修正值、單位、比較分母、官方來源與預估來源；缺少的欄位不要自行補猜。',
    quiz: { question: '同一季 GDP 成長估計由初值 1.6% 修正為 2.0%，在口徑不變下應如何描述？', options: ['新的一季額外成長 0.4%', 'GDP 的實際金額增加了 2.0 個百分點', '同一季的成長估計上修 0.4 個百分點'], answer: 2, explanation: '2.0% − 1.6% = 0.4 個百分點，是同一期估計值的修正；不代表新一期活動，也不能把百分點當成 GDP 金額單位。' },
    sources: [
      { label: '市場先生｜經濟指標與市場預期的主題閱讀', url: 'https://rich01.com/what-leading-indicator-lagging-3/', kind: 'reading' },
      { label: 'BEA｜GDP 初步季度估計的資料範圍', url: 'https://www.bea.gov/help/glossary/advance-quarterly-estimates', kind: 'official' },
      { label: 'BEA｜GDP 發布版本與後續更新', url: 'https://www.bea.gov/news/gdp-release-additional-information', kind: 'official' },
    ],
  },
];
